import { useInternationalization } from '@progress/kendo-react-intl'
import {
  type QueryFunctionContext,
  useInfiniteQuery,
} from '@tanstack/react-query'
import * as R from 'remeda'

import { graphql } from '~/gql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { addProp } from '~/utils'

import { useDocumentReportsInfiniteQueryKey } from './useDocumentReportsInfiniteQueryKey'

const document = graphql(`
  query DocumentReportListPage_DocumentReports(
    $brandAccountId: ID!
    $offset: Int!
  ) {
    documents(
      brandAccountId: $brandAccountId
      status: active
      pagination: { limit: 20, offset: $offset }
    ) {
      id
      title
      writer
      updatedAt
    }
  }
`)

type QueryKey = ReturnType<typeof useDocumentReportsInfiniteQueryKey>
type PageParam = number

export const useDocumentReportsInfiniteQuery = () => {
  const queryKey = useDocumentReportsInfiniteQueryKey()
  const graphQLRequest = useGraphQLRequest()
  const intl = useInternationalization()

  return useInfiniteQuery({
    queryFn: async ({
      pageParam: offset,
      queryKey: [_, variables],
    }: QueryFunctionContext<QueryKey, PageParam>) => {
      const result = await graphQLRequest({
        document,
        variables: { ...variables, offset },
      })

      return R.pipe(
        result.documents ?? [],
        R.map(
          addProp('updatedAtText', (documentReport) =>
            intl.formatDate(
              new Date(documentReport.updatedAt),
              'yyyy/MM/dd HH:mm',
            ),
          ),
        ),
      )
    },
    queryKey,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages): PageParam | undefined =>
      0 < lastPage.length ? allPages.flat().length : undefined,
  })
}

export type DocumentReport = QueryResultOf<
  typeof useDocumentReportsInfiniteQuery
>[number]
